import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faUsers,
  faChartBar,
  faSignOutAlt,
  faUser,
  faCog,
  faEye,
  faEdit,
  faTrash,
  faCheck,
  faTimes,
  faToggleOn,
  faToggleOff,
  faDownload,
  faRefresh,
  faSearch,
  faFilter,
  faBox,
  faTruck,
  faMoneyBillWave,
  faExclamationTriangle,
  faArrowUp,
  faArrowDown,
  faClock,
  faCalendarAlt,
  faChartLine,
  faBell,
  faChartPie,
  faBullhorn,
  faImage,
  faInfoCircle,
  faCube
} from '@fortawesome/free-solid-svg-icons';

import ManageUsers from './ManageUsers';
import Reports from './Reports';
import ManageAnnouncements from './ManageAnnouncements';
import ManagePromos from './ManagePromos';
import ManageShipments from '../Shared/ManageShipments';
import ManageBundles from './ManageBundles';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../../services/userService';
import { shipmentService } from '../../../services/shipmentService';
import { authService } from '../../../services/authService';
import { paymentsService } from '../../../services/paymentsService';
import { useAuth } from '../../../AuthContext';
import ErrorBoundary from './ErrorBoundary';
import useWelcomeOnce from '../../../hooks/useWelcomeOnce';
import { useUIStore } from '../../../store/uiStore';
import AnnouncementBanner from '@/components/AnnouncementBanner';
import UserSearch from '@/components/common/UserSearch';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const { currentUser, isAdmin, loading: authLoading } = useAuth();
  const [sessionChecked, setSessionChecked] = useState(false);
  const navigate = useNavigate();
  useWelcomeOnce('admin_dashboard', 'Welcome! 🛠️ Monitor shipments & manage users.');
  const { pushToast } = useUIStore();
  const statusMapRef = useRef({});
  const [error, setError] = useState('');


  const [stats, setStats] = useState({
    totalUsers: 0,
    totalShipments: 0,
    todayShipments: 0,
    pendingApprovals: 0,
    totalRevenue: 0,
    totalSubRevenue: 0,
    grandTotalRevenue: 0,
    activeSubscriptions: 0,
    expiredSubscriptions: 0,
    subscriptionStats: { monthly: 0, quarterly: 0, yearly: 0 },
    todayRevenue: 0,
    recentTransactions: []
  });

  useEffect(() => {
    if (!authLoading) {
      setSessionChecked(true);
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchStats = async () => {
      try {
        setError('');
        const token = localStorage.getItem('token');
        if (!token) return;

        // Fetch stats with individual try-catches to prevent one 404 from breaking everything
        const [userStats, shipmentStats, paymentStats] = await Promise.all([
          userService.getStats().catch(err => {
            console.warn('User stats failed:', err);
            return { totalUsers: 0 };
          }),
          shipmentService.getStats(token).catch(err => {
            console.warn('Shipment stats failed:', err);
            return { totalShipments: 0, todayShipments: 0, pendingCount: 0 };
          }),
          paymentsService.getTransactions(token).catch(err => {
            console.warn('Payment transactions failed:', err);
            return { todayRevenue: 0, transactions: [] };
          })
        ]);

        setStats({
          totalUsers: userStats?.totalUsers || 0,
          totalShipments: shipmentStats?.totalShipments || 0,
          todayShipments: shipmentStats?.todayShipments || 0,
          pendingApprovals: shipmentStats?.pendingCount || 0,
          totalRevenue: shipmentStats?.totalRevenue || 0,
          totalSubRevenue: shipmentStats?.totalSubRevenue || 0,
          grandTotalRevenue: shipmentStats?.grandTotalRevenue || 0,
          activeSubscriptions: shipmentStats?.activeSubscriptions || 0,
          expiredSubscriptions: shipmentStats?.expiredSubscriptions || 0,
          subscriptionStats: shipmentStats?.subscriptionStats || { monthly: 0, quarterly: 0, yearly: 0 },
          todayRevenue: paymentStats?.todayRevenue || 0,
          recentTransactions: paymentStats?.transactions || []
        });
      } catch (err) {
        const msg = err?.response?.data?.msg || err?.message || 'Failed to fetch admin stats';
        setError(`Admin stats error: ${msg}`);
        console.warn('Failed to fetch admin stats:', err);
      }
    };
    fetchStats();
  }, [isAdmin]);

  // Real-time shipment status updates via SSE
  useEffect(() => {
    if (!isAdmin) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const es = shipmentService.subscribeStream(token);
    es.onmessage = (evt) => {
      try {
        const payload = JSON.parse(evt.data || '{}');
        const shipments = payload?.shipments || payload || [];
        shipments.forEach((s) => {
          const id = s.trackingCode || s._id || s.id;
          const curr = (s.status || '').trim();
          const prev = statusMapRef.current[id];
          if (!prev) {
            statusMapRef.current[id] = curr;
            return;
          }
          if (prev !== curr) {
            statusMapRef.current[id] = curr;
            const type = curr.toLowerCase() === 'delivered' ? 'success' : 'info';
            pushToast(`Shipment ${id} status changed to ${curr}`, type, 2500, { ariaLabel: 'Shipment status update' });
          }
        });
      } catch (e) {
        console.warn('Admin stream parse error', e);
      }
    };
    return () => { try { es.close(); } catch { } };
  }, [pushToast]);

  const handleExport = () => {
    const dataStr = JSON.stringify(stats, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `admin-report-${new Date().toISOString().split('T')[0]}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleLogout = async () => {
    try {
      await authService.signOut();
    } catch (e) {
      console.warn('Sign out encountered an issue:', e);
    } finally {
      navigate('/auth');
    }
  };

  const renderDashboard = () => (
    <div className="space-y-8 text-white">
      {/* Overview Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-red-600 to-black p-6 rounded-2xl shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-white/20">
              <FontAwesomeIcon icon={faUsers} className="text-2xl" />
            </div>
          </div>
          <p className="text-red-100 text-sm font-medium">Total Users</p>
          <p className="text-4xl font-bold">{stats.totalUsers}</p>
        </div>

        <div className="bg-gradient-to-br from-red-600 to-black p-6 rounded-2xl shadow-lg cursor-pointer hover:scale-[1.02] transition-transform" onClick={() => setActiveTab('revenue')}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-white/20">
              <FontAwesomeIcon icon={faMoneyBillWave} className="text-2xl" />
            </div>
          </div>
          <p className="text-red-100 text-sm font-medium">Today's Revenue</p>
          <p className="text-4xl font-bold">₦{stats.todayRevenue?.toLocaleString()}</p>
        </div>

        <div
          onClick={() => setActiveTab('shipments')}
          className="bg-gradient-to-br from-red-600 to-black p-6 rounded-2xl shadow-lg cursor-pointer hover:scale-[1.02] transition-transform"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-white/20">
              <FontAwesomeIcon icon={faBox} className="text-2xl" />
            </div>
          </div>
          <p className="text-red-100 text-sm font-medium">Total Shipments</p>
          <p className="text-4xl font-bold">{stats.totalShipments}</p>
          <p className="text-sm text-red-100 mt-2 flex items-center">
            <FontAwesomeIcon icon={faArrowUp} className="mr-1 text-xs" />
            {stats.todayShipments} today
          </p>
        </div>

        <div className="bg-gradient-to-br from-red-600 to-black p-6 rounded-2xl shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-white/20">
              <FontAwesomeIcon icon={faBell} className="text-2xl" />
            </div>
          </div>
          <p className="text-red-100 text-sm font-medium">Pending Actions</p>
          <p className="text-4xl font-bold">{stats.pendingApprovals}</p>
        </div>
      </div>

      {/* User Search Feature */}
      <div className="text-gray-900">
        <UserSearch />
      </div>

      {/* Quick Action Grid */}
      <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-gray-900">
        <h2 className="text-xl font-bold mb-6">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <button
            onClick={() => setActiveTab('revenue')}
            className="flex flex-col items-center p-6 rounded-2xl bg-gray-50 hover:bg-red-50 hover:text-red-600 transition-all group"
          >
            <div className="p-4 rounded-xl bg-white shadow-sm mb-3 group-hover:scale-110 transition-transform">
              <FontAwesomeIcon icon={faMoneyBillWave} className="text-2xl text-green-500" />
            </div>
            <span className="text-sm font-bold text-center">Revenue</span>
          </button>

          <button
            onClick={() => setActiveTab('users')}
            className="flex flex-col items-center p-6 rounded-2xl bg-gray-50 hover:bg-red-50 hover:text-red-600 transition-all group"
          >
            <div className="p-4 rounded-xl bg-white shadow-sm mb-3 group-hover:scale-110 transition-transform">
              <FontAwesomeIcon icon={faUsers} className="text-2xl text-blue-500" />
            </div>
            <span className="text-sm font-bold text-center">Manage Users</span>
          </button>

          <button
            onClick={() => setActiveTab('shipments')}
            className="flex flex-col items-center p-6 rounded-2xl bg-gray-50 hover:bg-red-50 hover:text-red-600 transition-all group"
          >
            <div className="p-4 rounded-xl bg-white shadow-sm mb-3 group-hover:scale-110 transition-transform">
              <FontAwesomeIcon icon={faBox} className="text-2xl text-red-500" />
            </div>
            <span className="text-sm font-bold text-center">Shipments</span>
          </button>

          <button
            onClick={() => navigate('/dashboard/admin/tracking')}
            className="flex flex-col items-center p-6 rounded-2xl bg-gray-50 hover:bg-red-50 hover:text-red-600 transition-all group"
          >
            <div className="p-4 rounded-xl bg-white shadow-sm mb-3 group-hover:scale-110 transition-transform">
              <FontAwesomeIcon icon={faTruck} className="text-2xl text-orange-500" />
            </div>
            <span className="text-sm font-bold text-center">Tracking</span>
          </button>

          <button
            onClick={() => navigate('/dashboard/admin/pricing')}
            className="flex flex-col items-center p-6 rounded-2xl bg-gray-50 hover:bg-red-50 hover:text-red-600 transition-all group"
          >
            <div className="p-4 rounded-xl bg-white shadow-sm mb-3 group-hover:scale-110 transition-transform">
              <FontAwesomeIcon icon={faCog} className="text-2xl text-gray-500" />
            </div>
            <span className="text-sm font-bold text-center">Pricing</span>
          </button>

          <button
            onClick={() => setActiveTab('announcements')}
            className="flex flex-col items-center p-6 rounded-2xl bg-gray-50 hover:bg-red-50 hover:text-red-600 transition-all group"
          >
            <div className="p-4 rounded-xl bg-white shadow-sm mb-3 group-hover:scale-110 transition-transform">
              <FontAwesomeIcon icon={faBullhorn} className="text-2xl text-purple-500" />
            </div>
            <span className="text-sm font-bold text-center">News</span>
          </button>

          <button
            onClick={() => setActiveTab('bundles')}
            className="flex flex-col items-center p-6 rounded-2xl bg-gray-50 hover:bg-red-50 hover:text-red-600 transition-all group"
          >
            <div className="p-4 rounded-xl bg-white shadow-sm mb-3 group-hover:scale-110 transition-transform">
              <FontAwesomeIcon icon={faCube} className="text-2xl text-blue-400" />
            </div>
            <span className="text-sm font-bold text-center">Bundles</span>
          </button>

          <button
            onClick={() => navigate('/dashboard/admin/reports')}
            className="flex flex-col items-center p-6 rounded-2xl bg-gray-50 hover:bg-red-50 hover:text-red-600 transition-all group"
          >
            <div className="p-4 rounded-xl bg-white shadow-sm mb-3 group-hover:scale-110 transition-transform">
              <FontAwesomeIcon icon={faChartBar} className="text-2xl text-indigo-500" />
            </div>
            <span className="text-sm font-bold text-center">Reports</span>
          </button>

          <button
            onClick={handleExport}
            className="flex flex-col items-center p-6 rounded-2xl bg-gray-50 hover:bg-red-50 hover:text-red-600 transition-all group"
          >
            <div className="p-4 rounded-xl bg-white shadow-sm mb-3 group-hover:scale-110 transition-transform">
              <FontAwesomeIcon icon={faDownload} className="text-2xl text-gray-400" />
            </div>
            <span className="text-sm font-bold text-center">Export</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderRevenue = () => (
    <div className="space-y-8 animate-fadeIn">
      {/* Revenue Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-red-600 to-black p-6 rounded-2xl shadow-lg text-white">
          <p className="text-red-100 text-sm font-medium mb-1">Today's Revenue</p>
          <p className="text-3xl font-bold">₦{stats.todayRevenue?.toLocaleString()}</p>
          <div className="mt-4 flex items-center text-xs text-red-100 bg-white/10 w-fit px-2 py-1 rounded-full">
            <FontAwesomeIcon icon={faClock} className="mr-1" />
            Live tracking
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <p className="text-gray-500 text-sm font-medium mb-1">Total Revenue</p>
          <p className="text-3xl font-bold text-gray-900">₦{stats.grandTotalRevenue?.toLocaleString()}</p>
          <div className="mt-4 flex items-center text-xs text-blue-600 bg-blue-50 w-fit px-2 py-1 rounded-full">
            <FontAwesomeIcon icon={faChartLine} className="mr-1" />
            Overall Growth
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <p className="text-gray-500 text-sm font-medium mb-1">Subscriptions</p>
          <p className="text-3xl font-bold text-blue-600">₦{stats.totalSubRevenue?.toLocaleString()}</p>
          <p className="text-xs text-gray-400 mt-2">{stats.activeSubscriptions} active subscribers</p>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <p className="text-gray-500 text-sm font-medium mb-1">Shipments</p>
          <p className="text-3xl font-bold text-red-600">₦{stats.totalRevenue?.toLocaleString()}</p>
          <p className="text-xs text-gray-400 mt-2">{stats.totalShipments} total shipments</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Recent Transactions Table */}
        <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-red-600" />
                Live Transaction Feed
              </h3>
              <p className="text-xs text-gray-400 mt-1">Real-time view of Ecommerce & Bundle payments</p>
            </div>
            <button
              onClick={() => {
                const token = localStorage.getItem('token');
                paymentsService.getTransactions(token).then(data => {
                  setStats(prev => ({
                    ...prev,
                    todayRevenue: data?.todayRevenue || 0,
                    recentTransactions: data?.transactions || []
                  }));
                });
              }}
              className="p-2 text-gray-400 hover:text-red-600 transition-colors bg-gray-50 rounded-lg"
              title="Refresh transactions"
            >
              <FontAwesomeIcon icon={faRefresh} />
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-4 font-semibold text-gray-600 text-sm uppercase tracking-wider">User</th>
                  <th className="pb-4 font-semibold text-gray-600 text-sm uppercase tracking-wider">Type</th>
                  <th className="pb-4 font-semibold text-gray-600 text-sm uppercase tracking-wider">Description</th>
                  <th className="pb-4 font-semibold text-gray-600 text-sm uppercase tracking-wider">Amount</th>
                  <th className="pb-4 font-semibold text-gray-600 text-sm uppercase tracking-wider">Date</th>
                  <th className="pb-4 font-semibold text-gray-600 text-sm uppercase tracking-wider text-right">Ref</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {stats.recentTransactions?.length > 0 ? (
                  stats.recentTransactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50 transition-colors group">
                      <td className="py-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-red-50 text-red-600 flex items-center justify-center mr-3 text-xs font-bold">
                            {tx.userName.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-900">{tx.userName}</span>
                        </div>
                      </td>
                      <td className="py-4">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${tx.type === 'Ecommerce Subscription' ? 'bg-blue-100 text-blue-700' :
                          tx.type === 'Bundle Purchase' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                          {tx.type === 'Ecommerce Subscription' ? 'Ecommerce' : 'Bundle'}
                        </span>
                      </td>
                      <td className="py-4 text-gray-600 text-sm">{tx.plan}</td>
                      <td className="py-4">
                        <span className="font-bold text-gray-900">₦{tx.amount?.toLocaleString()}</span>
                      </td>
                      <td className="py-4">
                        <div className="text-gray-900 text-sm font-medium">{new Date(tx.date).toLocaleDateString()}</div>
                        <div className="text-gray-400 text-[10px]">{new Date(tx.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td className="py-4 text-right">
                        <span className="text-gray-400 text-xs font-mono bg-gray-50 px-2 py-1 rounded tracking-tighter">
                          {tx.reference?.slice(-8) || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-12 text-center text-gray-400 italic">
                      <div className="flex flex-col items-center">
                        <FontAwesomeIcon icon={faMoneyBillWave} className="text-3xl mb-3 opacity-20" />
                        <p>No recent transactions found</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Subscription Health Summary */}
        <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-around">
            <div className="text-center group">
              <div className="w-24 h-24 rounded-full border-8 border-green-50 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <span className="text-2xl font-bold text-green-600">{stats.activeSubscriptions}</span>
              </div>
              <p className="text-sm font-bold text-gray-900 uppercase tracking-wider">Active</p>
              <p className="text-xs text-gray-400">Paying customers</p>
            </div>
            <div className="text-center group">
              <div className="w-24 h-24 rounded-full border-8 border-red-50 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <span className="text-2xl font-bold text-red-600">{stats.expiredSubscriptions}</span>
              </div>
              <p className="text-sm font-bold text-gray-900 uppercase tracking-wider">Inactive</p>
              <p className="text-xs text-gray-400">Churned/Expired</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'users':
        return <ManageUsers />;
      case 'reports':
        return <Reports />;
      case 'revenue':
        return renderRevenue();
      case 'announcements':
        return <ManageAnnouncements />;
      case 'promos':
        return <ManagePromos />;
      case 'bundles':
        return <ManageBundles />;
      case 'shipments':
        return <ManageShipments />;
      default:
        return renderDashboard();
    }
  };

  if (!sessionChecked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-700">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{currentUser?.name}</p>
                <p className="text-xs text-gray-500">Administrator</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <FontAwesomeIcon icon={faUser} className="text-red-600" />
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <AnnouncementBanner targetAudience="admin" />
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-64">
            <div className="bg-white rounded-xl shadow-lg p-4 sticky top-6">
              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-all ${activeTab === 'dashboard'
                    ? 'bg-red-100 text-red-700 shadow-md'
                    : 'text-gray-700 hover:bg-gray-100 hover:shadow'
                    }`}
                >
                  <FontAwesomeIcon icon={faTachometerAlt} className="mr-3" />
                  <span>Dashboard</span>
                </button>
                <button
                  onClick={() => setActiveTab('revenue')}
                  className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-all ${activeTab === 'revenue'
                    ? 'bg-red-100 text-red-700 shadow-md'
                    : 'text-gray-700 hover:bg-gray-100 hover:shadow'
                    }`}
                >
                  <FontAwesomeIcon icon={faMoneyBillWave} className="mr-3 text-green-500" />
                  <span>Revenue & Subs</span>
                </button>
                <button
                  onClick={() => setActiveTab('users')}
                  className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-all ${activeTab === 'users'
                    ? 'bg-red-100 text-red-700 shadow-md'
                    : 'text-gray-700 hover:bg-gray-100 hover:shadow'
                    }`}
                >
                  <FontAwesomeIcon icon={faUsers} className="mr-3" />
                  <span>Users</span>
                </button>
                <button
                  onClick={() => setActiveTab('shipments')}
                  className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-all ${activeTab === 'shipments'
                    ? 'bg-red-100 text-red-700 shadow-md'
                    : 'text-gray-700 hover:bg-gray-100 hover:shadow'
                    }`}
                >
                  <FontAwesomeIcon icon={faBox} className="mr-3" />
                  <span>All Shipments</span>
                </button>

                <button
                  onClick={() => navigate('/dashboard/admin/pricing')}
                  className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-all text-gray-700 hover:bg-gray-100 hover:shadow`}
                >
                  <FontAwesomeIcon icon={faCog} className="mr-3" />
                  <span>Pricing Settings</span>
                </button>
                <button
                  onClick={() => navigate('/dashboard/admin/status-descriptions')}
                  className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-all text-gray-700 hover:bg-gray-100 hover:shadow`}
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="mr-3" />
                  <span>Status Descriptions</span>
                </button>
                <button
                  onClick={() => setActiveTab('announcements')}
                  className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-all ${activeTab === 'announcements'
                    ? 'bg-red-100 text-red-700 shadow-md'
                    : 'text-gray-700 hover:bg-gray-100 hover:shadow'
                    }`}
                >
                  <FontAwesomeIcon icon={faBullhorn} className="mr-3" />
                  <span>Announcements</span>
                </button>

                <button
                  onClick={() => setActiveTab('bundles')}
                  className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-all ${activeTab === 'bundles'
                    ? 'bg-red-100 text-red-700 shadow-md'
                    : 'text-gray-700 hover:bg-gray-100 hover:shadow'
                    }`}
                >
                  <FontAwesomeIcon icon={faCube} className="mr-3" />
                  <span>Manage Bundles</span>
                </button>
                <button
                  onClick={() => setActiveTab('promos')}
                  className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-all ${activeTab === 'promos'
                    ? 'bg-red-100 text-red-700 shadow-md'
                    : 'text-gray-700 hover:bg-gray-100 hover:shadow'
                    }`}
                >
                  <FontAwesomeIcon icon={faImage} className="mr-3" />
                  <span>Promos</span>
                </button>
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {error && (
              <div className="mb-4 px-4 py-3 bg-red-50 text-red-700 border border-red-200 rounded-xl flex items-center justify-between shadow-sm animate-shake">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3" />
                  <span>{error}</span>
                </div>
                {error.includes('Access denied') && (
                  <button
                    onClick={handleLogout}
                    className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Refresh Session
                  </button>
                )}
              </div>
            )}
            <ErrorBoundary>
              {renderContent()}
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
